import axios from 'axios';

// Parse a project's monetary value into a plain number (in the project's own currency).
// Handles numeric field_10_raw as well as European-formatted strings like "€1.275.058".
export const parseProjectValue = (project) => {
  const raw = project?.field_10_raw ?? project?.value ?? project?.field_10;
  if (typeof raw === 'number') return raw;
  if (typeof raw === 'string' && raw.trim() !== '') {
    let cleaned = raw.replace(/[€$£\s]/g, '');
    if (cleaned.includes('.') && !cleaned.includes(',')) {
      cleaned = cleaned.replace(/\./g, '');
    } else if (cleaned.includes(',')) {
      cleaned = cleaned.replace(/\./g, '').replace(',', '.');
    }
    const n = parseFloat(cleaned);
    return isNaN(n) ? 0 : n;
  }
  return 0;
};

export const getProjectCurrency = (project) => {
  const c = (project?.contract_currency || 'EUR').toUpperCase();
  return ['EUR', 'USD', 'GBP'].includes(c) ? c : 'EUR';
};

// Convert an amount in a given currency into EUR using EUR-based rates (rates[CUR] = units of CUR per 1 EUR).
export const convertToEUR = (amount, currency, rates) => {
  const cur = (currency || 'EUR').toUpperCase();
  if (cur === 'EUR' || !rates) return amount;
  const rate = rates[cur];
  if (!rate || rate === 0) return amount;
  return amount / rate;
};

// A project's value expressed in EUR.
export const getProjectValueEUR = (project, rates) => {
  return convertToEUR(parseProjectValue(project), getProjectCurrency(project), rates);
};

// Format a number as EUR with European thousand separators (e.g. €1.275.058).
export const formatEUR = (num) => {
  const n = typeof num === 'number' ? num : (parseFloat(num) || 0);
  return '€' + n.toLocaleString('de-DE', { maximumFractionDigits: 0 });
};

// Short EUR format: €4.5M, €230K, €950.
export const formatShort = (num) => {
  const n = typeof num === 'number' ? num : (parseFloat(num) || 0);
  const abs = Math.abs(n);
  const sign = n < 0 ? '-' : '';
  if (abs >= 1_000_000) {
    const v = n / 1_000_000;
    return `${sign}€${(abs / 1_000_000).toFixed(abs % 1_000_000 === 0 ? 0 : 1).replace(/\.0$/, '')}M`;
  }
  if (abs >= 1_000) {
    return `${sign}€${(abs / 1_000).toFixed(abs % 1_000 === 0 ? 0 : 1).replace(/\.0$/, '')}K`;
  }
  return formatEUR(n);
};

// Fetch & cache EUR-based exchange rates (24h localStorage cache).
let _ratesCache = null;
export const getFallbackRates = () => ({ EUR: 1.0, USD: 1.08, GBP: 0.85 });

export const fetchExchangeRates = async (apiBase, token) => {
  if (_ratesCache) return _ratesCache;
  try {
    const saved = localStorage.getItem('exchangeRates');
    if (saved) {
      const parsed = JSON.parse(saved);
      if (parsed.fetched_at && Date.now() - new Date(parsed.fetched_at).getTime() < 86400000 && parsed.rates) {
        _ratesCache = parsed.rates;
        return _ratesCache;
      }
    }
  } catch (e) { /* ignore */ }

  try {
    const res = await axios.get(`${apiBase}/settings/exchange-rates`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const rates = res.data?.rates || getFallbackRates();
    _ratesCache = rates;
    localStorage.setItem('exchangeRates', JSON.stringify({ rates, fetched_at: new Date().toISOString() }));
    return rates;
  } catch (e) {
    return getFallbackRates();
  }
};
